import { P } from "@angular/cdk/keycodes";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin, Subject } from "rxjs";
import { switchMap, takeUntil, tap } from "rxjs/operators";
import { AccountUserGroupService } from "src/app/account-user-group/account-user-group.service";
import { NotificationService } from "src/app/notifications/notification.service";
import { SessionService } from "src/app/session.service";
import { UiService } from "src/app/ui/ui.service";

@Component({
  selector: "app-user-group",
  templateUrl: "./user-group.component.html",
  styleUrls: ["./user-group.component.scss"],
})
export class UserGroupComponent implements OnInit, OnDestroy {
  userGroup: any;
  userGroupForm!: FormGroup;
  members: any;
  unusedTeamMembers: any;
  accountUsers: any;
  isCreateGroup!: boolean;
  initialAccountUsers: any;
  private onComponentDestroy = new Subject();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private accountUserGroupService: AccountUserGroupService,
    private sessionService: SessionService,
    private notificationService: NotificationService,
    private uiService: UiService,
  ) {}

  ngOnInit(): void {
    this.userGroup = this.route.snapshot.data.userGroup ?? {
      name: null,
      accountUsers: [],
    };
    this.isCreateGroup = !this.route.snapshot.data.userGroup;

    if (!this.isCreateGroup) {
      this.initialAccountUsers =
        this.route.snapshot.data.userGroup.accountUsers;
    }

    this.userGroup.accountUsers = this.userGroup.accountUsers.map(
      (accountUser: any) => {
        return {
          ...accountUser,
          name:
            accountUser.user.first_name || accountUser.user.last_name
              ? `${accountUser.user.first_name} ${accountUser.user.last_name}`
              : "No name",
        };
      },
    );

    this.userGroupForm = new FormGroup({
      name: new FormControl(this.userGroup?.name, [Validators.required]),
    });

    this.accountUsers = this.route.snapshot.data.accountUsers.map(
      (accountUser: any) => {
        return {
          ...accountUser,
          name:
            accountUser.user.first_name || accountUser.user.last_name
              ? `${accountUser.user.first_name} ${accountUser.user.last_name}`
              : "No name",
        };
      },
    );

    this.initUnusedTeamMembers();
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  get currentAccount() {
    return this.sessionService.currentAccountValue!;
  }

  initUnusedTeamMembers() {
    this.unusedTeamMembers = this.accountUsers.filter((member: any) => {
      return !this.userGroup.accountUsers.find((accountUser: any) => {
        return member.id === accountUser.id;
      });
    });
  }

  cancel() {
    this.router.navigate(["users", "groups", "list"]);
  }

  createNewGroup() {
    this.uiService
      .waitForEvent(
        this.accountUserGroupService
          .createAccountUserGroup(this.currentAccount.accountID, {
            ...this.userGroup,
            name: this.userGroupForm.controls.name.value,
          })
          .pipe(
            tap(
              (res: any) => {
                if (this.userGroup.accountUsers?.length) {
                  const addedAccountUsersObservableArr =
                    this.userGroup.accountUsers.map((addedAccountUser: any) => {
                      return this.accountUserGroupService.createAccountUserGroupUser(
                        this.currentAccount.accountID,
                        {
                          account_user_group_id: res.id,
                          account_user_id: addedAccountUser.id,
                        },
                      );
                    });

                  this.uiService
                    .waitForEvent(forkJoin([...addedAccountUsersObservableArr]))
                    .pipe(takeUntil(this.onComponentDestroy))
                    .subscribe(
                      () => {
                        this.notificationService.openSuccessSnackBar(
                          "User group created",
                        );
                        this.router.navigate(["users", "groups", "list"]);
                      },
                      () => {
                        this.notificationService.openErrorSnackBar(
                          "Error creating user group",
                        );
                      },
                    );
                } else {
                  this.notificationService.openSuccessSnackBar(
                    "User group created",
                  );
                  this.router.navigate(["users", "groups", "list"]);
                }
              },
              (err) => {
                this.notificationService.openErrorSnackBar(
                  "Error creating user group",
                );
              },
            ),
            switchMap(() =>
              this.accountUserGroupService.getAccountUserGroups(
                this.currentAccount.accountID,
              ),
            ),
          ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((groups) => {
        this.sessionService.updateGroups(groups);
      });
  }

  updateGroup() {
    this.uiService
      .waitForEvent(
        this.accountUserGroupService
          .updateAccountUserGroup(this.currentAccount.accountID, {
            ...this.userGroup,
            name: this.userGroupForm.controls.name.value,
          })
          .pipe(
            tap(
              () => {
                const addedAccountUsersObservableArr =
                  this.getAddedAccountUsers().map((addedAccountUser: any) => {
                    return this.accountUserGroupService.createAccountUserGroupUser(
                      this.currentAccount.accountID,
                      {
                        account_user_group_id: this.userGroup.id,
                        account_user_id: addedAccountUser.id,
                      },
                    );
                  });
                const removedAccountUsersObserableArr =
                  this.getRemovedAccountUsers().map(
                    (removedAccountUser: any) => {
                      return this.accountUserGroupService.deleteAccountUserGroupUser(
                        this.currentAccount.accountID,
                        {
                          id: removedAccountUser._pivot_id,
                          account_user_group_id: this.userGroup.id,
                          account_user_id: removedAccountUser.id,
                        },
                      );
                    },
                  );
                if (
                  removedAccountUsersObserableArr.length ||
                  addedAccountUsersObservableArr.length
                ) {
                  this.uiService
                    .waitForEvent(
                      forkJoin([
                        ...addedAccountUsersObservableArr,
                        ...removedAccountUsersObserableArr,
                      ]),
                    )
                    .pipe(takeUntil(this.onComponentDestroy))
                    .subscribe(
                      () => {
                        this.notificationService.openSuccessSnackBar(
                          "User group updated",
                        );
                        this.router.navigate(["users", "groups", "list"]);
                      },
                      (err) => {
                        this.notificationService.openErrorSnackBar(
                          "Error updating user group",
                        );
                      },
                    );
                } else {
                  this.notificationService.openSuccessSnackBar(
                    "User group updated",
                  );
                  this.router.navigate(["users", "groups", "list"]);
                }
              },
              (err) => {
                this.notificationService.openErrorSnackBar(
                  "Error updating user group",
                );
              },
            ),
            switchMap(() =>
              this.accountUserGroupService.getAccountUserGroups(
                this.currentAccount.accountID,
              ),
            ),
          ),
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((groups) => {
        this.sessionService.updateGroups(groups);
      });
  }

  removeMember(member: any) {
    this.userGroup.accountUsers = this.userGroup.accountUsers.filter(
      (accountUser: any) => accountUser.id !== member.id,
    );

    this.initUnusedTeamMembers();
  }

  addMember(member: any) {
    this.userGroup.accountUsers.push(member);

    this.initUnusedTeamMembers();
  }

  getAddedAccountUsers() {
    return this.userGroup.accountUsers.filter((accountUser: any) => {
      return !this.initialAccountUsers.find(
        (initAccountUser: any) => accountUser.id === initAccountUser.id,
      );
    });
  }

  getRemovedAccountUsers() {
    return this.initialAccountUsers.filter((initAccountUser: any) => {
      return !this.userGroup.accountUsers.find(
        (accountUser: any) => accountUser.id === initAccountUser.id,
      );
    });
  }
}
